import * as React from "react"
import { Copy, Share2, QrCode, Users, CheckCircle, RefreshCw, UserPlus } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Checkbox } from "@/components/ui/checkbox"
import type { Group, User } from "@/types/domain.types"
import { logger } from "@/lib/logger"

interface GroupInviteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  group: Group
  onGenerateInvite?: () => Promise<string>
  onRefreshInvite?: () => Promise<string>
  onRemoveMember?: (userId: string) => Promise<void>
  onAddUsersToGroup?: (userIds: string[]) => Promise<void>
  allUsers?: User[]
  isGenerating?: boolean
}

export const GroupInvite: React.FC<GroupInviteProps> = ({
  open,
  onOpenChange,
  group,
  onGenerateInvite,
  onRefreshInvite,
  onRemoveMember: _onRemoveMember,
  onAddUsersToGroup,
  allUsers = [],
  isGenerating = false,
}) => {
  const [copiedCode, setCopiedCode] = React.useState(false)
  const [copiedLink, setCopiedLink] = React.useState(false)
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([])

  const inviteLink = group.invite_code
    ? `${window.location.origin}/join/${group.invite_code}`
    : ""

  const handleCopyCode = async () => {
    if (group.invite_code) {
      try {
        await navigator.clipboard.writeText(group.invite_code)
        setCopiedCode(true)
        setTimeout(() => setCopiedCode(false), 2000)
      } catch (error) {
        logger.error("Failed to copy invite code:", error)
      }
    }
  }

  const handleCopyLink = async () => {
    if (inviteLink) {
      try {
        await navigator.clipboard.writeText(inviteLink)
        setCopiedLink(true)
        setTimeout(() => setCopiedLink(false), 2000)
      } catch (error) {
        logger.error("Failed to copy invite link:", error)
      }
    }
  }

  const handleShare = async () => {
    if (navigator.share && inviteLink) {
      try {
        await navigator.share({
          title: `Join ${group.name} on Expenses`,
          text: `You've been invited to join ${group.name} for tracking shared expenses.`,
          url: inviteLink,
        })
      } catch (error) {
        logger.error("Failed to share:", error)
      }
    }
  }

  const handleGenerateInvite = async () => {
    if (onGenerateInvite) {
      try {
        await onGenerateInvite()
      } catch (error) {
        logger.error("Failed to generate invite:", error)
      }
    }
  }

  const handleRefreshInvite = async () => {
    if (onRefreshInvite) {
      try {
        await onRefreshInvite()
      } catch (error) {
        logger.error("Failed to refresh invite:", error)
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Invite to {group.name}
          </DialogTitle>
          <DialogDescription>
            Share this group with friends and family to start tracking expenses together.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Invite Methods */}
          {group.invite_code ? (
            <div className="space-y-4">
              {/* Invite Code */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Invite Code</CardTitle>
                  <CardDescription>
                    Share this code for people to join your group
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Input
                      value={group.invite_code}
                      readOnly
                      className="font-mono text-center text-lg"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopyCode}
                      className={copiedCode ? "text-green-600" : ""}
                    >
                      {copiedCode ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleRefreshInvite}
                      disabled={isGenerating}
                      className="flex-1"
                    >
                      <RefreshCw className={`mr-2 h-4 w-4 ${isGenerating ? 'animate-spin' : ''}`} />
                      Refresh Code
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Invite Link */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Invite Link</CardTitle>
                  <CardDescription>
                    Share this link for easy one-click joining
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Input
                      value={inviteLink}
                      readOnly
                      className="text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopyLink}
                      className={copiedLink ? "text-green-600" : ""}
                    >
                      {copiedLink ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    {typeof window !== 'undefined' && 'share' in navigator && typeof (navigator as any).share === 'function' && (
                      <Button
                        variant="outline"
                        onClick={handleShare}
                        className="flex-1"
                      >
                        <Share2 className="mr-2 h-4 w-4" />
                        Share
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="mb-4">
                  <QrCode className="h-12 w-12 mx-auto text-muted-foreground" />
                </div>
                <h3 className="font-semibold mb-2">No Invite Code Yet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Generate an invite code to start inviting people to this group.
                </p>
                <Button
                  onClick={handleGenerateInvite}
                  disabled={isGenerating}
                >
                  {isGenerating ? "Generating..." : "Generate Invite Code"}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Current Members */}
          {group.members && group.members.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  Current Members
                  <Badge variant="secondary">
                    {group.members.length} member{group.members.length !== 1 ? 's' : ''}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  People who are currently part of this group
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {group.members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={member.avatar_url} />
                          <AvatarFallback>
                            {member.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{member.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {member.email}
                          </div>
                        </div>
                      </div>
                      {member.role && (
                        <Badge variant={member.role === 'admin' ? 'default' : 'secondary'}>
                          {member.role}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Add Users Section */}
          {allUsers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserPlus className="h-5 w-5" />
                  Add Users to Group
                </CardTitle>
                <CardDescription>
                  Select users to add to this group
                </CardDescription>
              </CardHeader>
              <CardContent>

                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-3">
                    <Label>Select Users to Add</Label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const memberIds = group.members?.map(m => m.id) || []
                          const availableUserIds = allUsers
                            .filter(u => !memberIds.includes(u.id))
                            .map(u => u.id)
                          setSelectedUserIds(availableUserIds)
                        }}
                      >
                        Select All
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedUserIds([])}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>

                  <div className="max-h-48 overflow-y-auto space-y-2 border rounded-lg p-3">
                    {allUsers
                      .filter(user => !group.members?.some(m => m.id === user.id))
                      .map((user) => (
                        <label
                          key={user.id}
                          className="flex items-center gap-3 p-2 rounded hover:bg-accent cursor-pointer"
                        >
                          <Checkbox
                            checked={selectedUserIds.includes(user.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedUserIds([...selectedUserIds, user.id])
                              } else {
                                setSelectedUserIds(selectedUserIds.filter(id => id !== user.id))
                              }
                            }}
                          />
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar_url} />
                            <AvatarFallback>
                              {user.name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="font-medium text-sm">{user.name}</div>
                            <div className="text-xs text-muted-foreground">{user.email}</div>
                          </div>
                        </label>
                      ))}
                  </div>

                  {selectedUserIds.length > 0 && (
                    <Button
                      className="w-full"
                      onClick={async () => {
                        if (onAddUsersToGroup) {
                          await onAddUsersToGroup(selectedUserIds)
                          setSelectedUserIds([])
                        }
                      }}
                    >
                      <UserPlus className="mr-2 h-4 w-4" />
                      Add {selectedUserIds.length} User{selectedUserIds.length !== 1 ? 's' : ''} to Group
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}